<template>
  <remote-modal
    ref="modal"
    modal-title="Destination select (Study Visit)"
    @details-loaded="onFormLoaded"
  >
    <template #modal-content="{ modalState }">
      <template v-if="modalState.initialDataLoaded">
        <alert
          :message="errorMessage || undefined"
          :dismissable="true"
          @dismiss="errorMessage = null"
        />

        <div
          v-if="participantNameVisible"
          class="mb-2"
        >
          <label class="form-label">Participant</label>
          <input
            v-model="destination.participantName"
            type="text"
            class="form-control form-control-sm"
            readonly
          >
        </div>

        <template v-if="!ncpView">
          <div class="mb-2">
            <label class="form-label required">Destination choice</label>
            <select
              ref="destinationChoiceSelect"
              class="form-select"
            >
              <option />
            </select>
          </div>
        </template>
        <div
          class="mb-2"
        >
          <label class="form-label required">Period</label>
          <select
            v-model="destination.periodId"
            class="form-select form-select-sm"
          >
            <option
              v-for="period of selectedDestinationPeriods"
              :key="period.id"
              :value="period.id"
            >
              {{ periodDescription(period) }}
            </option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label">Notes</label>
          <textarea
            v-model="destination.notes"
            rows="4"
            class="form-control form-control-sm"
          />
        </div>
      </template>
    </template>
    <template #modal-footer="{ modalState }">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="approveBtnDisabled(modalState)"
        @click.prevent="approveParticipant"
      >
        {{ editMode ? 'Update' : 'Approve' }}
      </button>
    </template>
  </remote-modal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import $ from 'jquery'
import Jsona from 'jsona'
import axios from 'axios'
import Rails from '@rails/ujs'
import RemoteModal from '../remote-modal.vue'
import Alert from '../alert.vue'
import type { DataFormat, GroupedDataFormat } from 'select2'
import type { GridID } from '../types'
import { getAxiosError } from '@/js/axios-utils'
import { isNonEmptyString } from '@/js/js-utils'

interface StudyVisitDestinationChoicePeriod {
  id: number;
  dates: string;
  location: string;
  language: string;
}

interface StudyVisitDestinationChoice {
  id: number;
  activityNumber: string;
  institutionName: string;
  periods: StudyVisitDestinationChoicePeriod[];
}

interface StudyVisitDestinationData {
  participantName?: string | null;
  selectedChoiceId: number | null;
  selectedPeriodId: number | null;
  notes: string | null;

  primaryChoices: StudyVisitDestinationChoice[];
  otherChoices: StudyVisitDestinationChoice[];
  links: {
    submit: string;
  };
}

interface StudyVisitDestination {
  participantName: string | null;
  placeId: number | null;
  periodId: number | null;
  notes: string | null;
}

interface ModalState {
  initialDataLoaded: boolean;
  isLoading: boolean;
}

const emit = defineEmits<{
  "success": []
}>()

const modal = useTemplateRef<InstanceType<typeof RemoteModal>>("modal")
const destinationChoiceSelect = useTemplateRef<HTMLSelectElement>("destinationChoiceSelect")

const buildDestination = (): StudyVisitDestination => ({
  participantName: null,
  placeId: null,
  periodId: null,
  notes: null,
})

const ncpView = ref<boolean>(false)
const editMode = ref<boolean>(false)
const errorMessage = ref<string | null>(null)
const submitUrl = ref<string | null>(null)
const primaryChoices = ref<StudyVisitDestinationChoice[]>([])
const otherChoices = ref<StudyVisitDestinationChoice[]>([])
const destination = ref<StudyVisitDestination>(buildDestination())
const destinationSelectEventAttached = ref<boolean>(false)

const selectedDestination = computed((): StudyVisitDestinationChoice | null => {
  if (destination.value.placeId) {
    const selectedChoice = primaryChoices.value.find(choice => choice.id == destination.value.placeId)
    if (selectedChoice) {
      return selectedChoice
    } else {
      const otherSelectedChoice = otherChoices.value.find(choice => choice.id == destination.value.placeId)
      if (otherSelectedChoice) {
        return otherSelectedChoice
      }
    }
  }
  return null
})

const selectedPeriod = computed((): StudyVisitDestinationChoicePeriod | null => {
  if (destination.value.periodId && selectedDestination.value) {
    const period = selectedDestination.value.periods.find(period => period.id == destination.value.periodId)
    if (period) {
      return period
    }
  }
  return null
})

const selectedDestinationPeriods = computed((): StudyVisitDestinationChoicePeriod[] => {
  return selectedDestination.value?.periods || []
})

const participantNameVisible = computed((): boolean => {
  return isNonEmptyString(destination.value.participantName)
})

const show = (url: string, applicationIds: GridID[] | null, isEditMode?: boolean): void => {
  errorMessage.value = null
  destinationSelectEventAttached.value = false
  editMode.value = isEditMode == true
  if (Array.isArray(applicationIds)) {
    modal.value?.showModal(url, {
      application_ids: applicationIds.join(',')
    }, {
      headers: {
        'Accept': 'application/vnd.api+json'
      },
    })
  } else {
    modal.value?.showModal(url)
  }
}

const hide = (): void => {
  modal.value?.hideModal()
}

const onFormLoaded = (data: any): void => {
  primaryChoices.value = []
  otherChoices.value = []
  ncpView.value = false

  destination.value = buildDestination()

  const jsdata = new Jsona().deserialize(data) as StudyVisitDestinationData | undefined
  if (jsdata && !Array.isArray(jsdata)) {
    if (isNonEmptyString(jsdata.links.submit)) {
      submitUrl.value = jsdata.links.submit
    }

    destination.value.participantName = isNonEmptyString(jsdata.participantName) ? jsdata.participantName : null

    if (Array.isArray(jsdata.primaryChoices)) {
      primaryChoices.value = jsdata.primaryChoices
    }

    if (Array.isArray(jsdata.otherChoices)) {
      otherChoices.value = jsdata.otherChoices
    }

    if (jsdata.selectedChoiceId) {
      destination.value.placeId = jsdata.selectedChoiceId
      if (jsdata.selectedPeriodId) {
        destination.value.periodId = jsdata.selectedPeriodId
      }
    }
    if (isNonEmptyString(jsdata.notes)) {
      destination.value.notes = jsdata.notes
    }
  }

  nextTick(() => {
    initFields()
  })
}

const initFields = (): void => {
  const destinationChoiceSelectEl = destinationChoiceSelect.value
  if (destinationChoiceSelectEl) {
    const data = [] as GroupedDataFormat[]

    const primaryItems = [] as DataFormat[]
    primaryChoices.value.forEach((choice) => {
      primaryItems.push({
        id: choice.id,
        text: `${choice.activityNumber} ${choice.institutionName}`,
      })
    })
    data.push({
      text: 'Primary choices',
      children: primaryItems
    })

    const otherItems = [] as DataFormat[]
    otherChoices.value.forEach((choice) => {
      otherItems.push({
        id: choice.id,
        text: `${choice.activityNumber} ${choice.institutionName}`,
      })
    })
    data.push({
      text: 'Other choices',
      children: otherItems
    })

    // clear selection box, otherwise previously populated items
    // are still preserved
    destinationChoiceSelectEl.innerHTML = ''
    destinationChoiceSelectEl.options.add(new Option('')) // required for placeholder to work

    $(destinationChoiceSelectEl).select2({
      placeholder: 'Select destination choice',
      allowClear: true,
      data: data,
      // templateResult: formatDestinationOption,
    })

    if (destination.value.placeId) {
      $(destinationChoiceSelectEl).val(destination.value.placeId)
      $(destinationChoiceSelectEl).trigger('change')
    }

    if (!destinationSelectEventAttached.value) {
      destinationSelectEventAttached.value = true
      $(destinationChoiceSelectEl).on('select2:select', (e: any) => {
        const result = e.params.data

        const selectedId = parseInt(result.id)
        if (!isNaN(selectedId)) {
          destination.value.placeId = selectedId
        } else {
          destination.value.placeId = null
        }
      })
    }
  }
}

const approveBtnDisabled = (modalState: ModalState): boolean => {
  if (!modalState.initialDataLoaded || modalState.isLoading) {
    return true
  }

  return selectedDestination.value == null || submitUrl.value == null
}

const approveParticipant = (): void => {
  errorMessage.value = null

  if (selectedDestination.value && selectedPeriod.value && isNonEmptyString(submitUrl.value)) {
    modal.value?.showLoadingIndicator(true)
    const choiceParams: any = {
      place_id: selectedDestination.value.id,
      period_id: selectedPeriod.value.id,
      notes: destination.value.notes,
    }

    axios.post(submitUrl.value, {
      destination_choice: choiceParams
    }, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken()
      },
    })
      .then(() => {
        emit('success')
      })
      .catch((error) => {
        errorMessage.value = getAxiosError(error) || 'An error occured'
      })
      .then(() => {
        modal.value?.showLoadingIndicator(false)
      })
  }
}

const periodDescription = (period: StudyVisitDestinationChoicePeriod): string => {
  return [
    period.dates,
    period.location,
    period.language,
  ].filter(value => isNonEmptyString(value))
    .join(', ')
}

defineExpose({
  hide,
  show,
})
</script>
